/**
 * Press release service for API interactions
 * Based on reportService.js but customized for press releases
 */
import {apiClient, fetchWithCache, createCRUDMethods} from './api';
import {API_BASE} from '../utils/constants'

class PressReleaseService {
  constructor() {
    this.baseURL = '/press-releases'; // Fixed: removed '/api' since apiClient already has it
    this.crud = createCRUDMethods(this.baseURL);
    
    console.log('- API_BASE:', API_BASE);
  }

  // ===== PRESS RELEASE CRUD OPERATIONS =====

  /**
   * Get press releases with filtering and pagination
   */
  async getPressReleases(options = {}) {
    const {
      status,
      priority,
      company,
      rss_source,
      limit = 100,
      skip = 0
    } = options;

    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (priority) params.append('priority', priority);
    if (company) params.append('company', company);  
    if (rss_source) params.append('rss_source', rss_source);
    params.append('limit', limit.toString());
    params.append('skip', skip.toString());

    const fullURL = `${this.baseURL}?${params.toString()}`;
    console.log('🔗 Making API call to:', fullURL);
    console.log('🔧 API base configuration:', apiClient.defaults?.baseURL);
    
    const response = await apiClient.get(fullURL);

    console.log('📡 Raw API response:', response);
    
    return response.data;
  }

  /**
   * Get a specific press release by ID
   */
  async getPressRelease(pressReleaseId) {
    const response = await fetchWithCache(`${this.baseURL}/${pressReleaseId}`);
    return response.data;
  }

  /**
   * Update a press release
   */
  async updatePressRelease(pressReleaseId, updateData) {
    const response = await apiClient.put(`${this.baseURL}/${pressReleaseId}`, updateData);
    return response.data;
  }

  /**
   * Delete a press release
   */
  async deletePressRelease(pressReleaseId) {
    const response = await apiClient.delete(`${this.baseURL}/${pressReleaseId}`);
    return response.data;
  }

  /**
   * Scrape content from press release URL (mirrors report scraping)
   */
  async scrapePressRelease(pressReleaseId) {
    const response = await apiClient.post(`${this.baseURL}/${pressReleaseId}/scrape`);
    return response.data;
  }

  // ===== RSS FEED MANAGEMENT =====

  /**
   * Create a new RSS feed
   */
  async createRSSFeed(feedData) {
    const response = await apiClient.post(`${this.baseURL}/feeds`, feedData);
    return response.data;
  }

  /**
   * Get RSS feeds with filtering
   */
  async getRSSFeeds(options = {}) {
    const {
      status,
      limit = 50,
      skip = 0
    } = options;

    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('limit', limit.toString());
    params.append('skip', skip.toString());
    
    // Add cache busting parameter to avoid stale data
    params.append('_refresh', Date.now().toString());

    const fullURL = `${this.baseURL}/feeds?${params.toString()}`;
    console.log('🔗 RSS Feeds API call:', fullURL);
    console.log('🔧 API base configuration:', apiClient.defaults?.baseURL);
    
    const response = await apiClient.get(fullURL);
    console.log('📡 Raw RSS Feeds API response:', response);
    
    return response.data;
  }

  /**
   * Delete an RSS feed
   */
  async deleteRSSFeed(feedId) {
    const response = await apiClient.delete(`${this.baseURL}/feeds/${feedId}`);
    return response.data;
  }

  /**
   * Refresh a specific RSS feed
   */
  async refreshRSSFeed(feedId) {
    const response = await apiClient.post(`${this.baseURL}/feeds/${feedId}/refresh`);
    return response.data;
  }

  /**
   * Refresh all RSS feeds
   */
  async refreshAllFeeds() {
    const response = await apiClient.post(`${this.baseURL}/bulk/refresh-all-feeds`);
    return response.data;
  }

  // ===== STATISTICS =====

  /**
   * Get press release statistics
   */
  async getStats() {
    const response = await apiClient.get(`${this.baseURL}/stats/overview`);
    return response.data;
  }

  // ===== ARTICLE GENERATION (Future implementation) =====

  /**
   * Generate article from press release (placeholder for future implementation)
   */
  async generateArticle(pressReleaseId) {
    const response = await apiClient.post(`${this.baseURL}/${pressReleaseId}/generate-article`);
    return response.data;
  }
}

// Export singleton instance
const pressReleaseService = new PressReleaseService();
export default pressReleaseService;